import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ActivatedRoute, Router } from '@angular/router';
import { NotaDTO } from '../../../../../../models/nota.model';
import { NotaService } from '../../../../../../services/financeiro/notas.service';
import { ItemsService } from '../../../../../../services/financeiro/item.service';
import { ItemDTO } from '../../../../../../models/item.model';

@Component({
  selector: 'app-itens-table',
  templateUrl: './itens-table.component.html',
  styleUrls: ['./itens-table.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class ItensTableComponent implements OnInit, OnDestroy {
  @Input() pessoaId: number | undefined;
  @Output() selectedItem = new EventEmitter<ItemDTO>();

  itens: ItemDTO[] = [];
  first = 0;
  rows = 5;
  totalRecords = 0;
  lastfield = 'id';
  lastSort = 'asc';
  searchCodigo: string = '';
  rangeDates: any[] = [];
  options = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Objetos de traducao
  placeholders = {
    pesquisar_codigo: '',
    pesquisar_data: ''
  };

  botoes = {
    buscar: '',
    baixar_pdf: ''
  };

  headers = {
    imagem: '',
    barras: '',
    nome: '',
    preco_venda: '',
    tipo_imposto: '',
    custo: '',
    saldo: '',
    tipo_item: '',
    acoes: ''
  };

  texts = {
    itens_por_pagina: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private router: Router,
    private itemService: ItemsService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    await this.findItem(0, this.rows, 'id,asc');
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.placeholders = {
      pesquisar_codigo: this.translate.instant('shared.itens-table.placeholders.pesquisar_codigo'),
      pesquisar_data: this.translate.instant('shared.itens-table.placeholders.pesquisar_data')
    };

    this.botoes = {
      buscar: this.translate.instant('shared.itens-table.botoes.buscar'),
      baixar_pdf: this.translate.instant('shared.itens-table.botoes.baixar_pdf')
    };

    this.headers = {
      imagem: this.translate.instant('shared.itens-table.headers.imagem'),
      barras: this.translate.instant('shared.itens-table.headers.barras'),
      nome: this.translate.instant('shared.itens-table.headers.nome'),
      preco_venda: this.translate.instant('shared.itens-table.headers.preco_venda'),
      tipo_imposto: this.translate.instant('shared.itens-table.headers.tipo_imposto'),
      custo: this.translate.instant('shared.itens-table.headers.custo'),
      saldo: this.translate.instant('shared.itens-table.headers.saldo'),
      tipo_item: this.translate.instant('shared.itens-table.headers.tipo_item'),
      acoes: this.translate.instant('shared.itens-table.headers.acoes')
    };

    this.texts = {
      itens_por_pagina: this.translate.instant('shared.itens-table.itens_por_pagina')
    };
  }

  findItem(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc'
  ): Promise<void> {
    return new Promise((resolve) => {
      const pessoaId = this.pessoaId ? `pessoaId=${this.pessoaId}` : '';

      const codigoMovimento = this.searchCodigo
        ? `&codigoNota=${this.searchCodigo}`
        : '';

      const filter =
        (pessoaId ? `${pessoaId}` : '') +
        (codigoMovimento ? `${codigoMovimento}` : '');

      this.itemService.get('/item', { page, size, sort }).subscribe(
        (response) => {
          this.itens = response.content;
          this.totalRecords = response.totalElements;
          this.first = page * size;
          resolve();
        },
        (error) => {
          console.error('Erro ao buscar clientes:', error);
          this.itens = [];
          this.totalRecords = 0;
          this.rangeDates = [];
        }
      );
    });
  }

  onPageChange(event: any) {
    const page = event.page; // Página atual a partir do paginator
    const size = event.rows; // Quantidade de itens por página
    this.findItem(page, size);
  }

  onRowsPerPageChange() {
    this.first = 0; // Reiniciar a página para o início
    this.findItem(0, this.rows); // Buscar a primeira página com o novo tamanho
  }

  customSort(event: SortEvent) {
    let sort = 'desc';
    if (event.order === 1) {
      sort = 'asc';
    } // 1 é ascendente, -1 é descendente}

    if (this.lastfield === event.field && this.lastSort === sort) {
      return;
    } // Essencial para não cair em loop infinito

    this.lastfield = String(event.field);
    this.lastSort = String(sort);

    this.findItem(0, this.rows, `${event.field},${sort}`);
  }

  onKeyDown(event: KeyboardEvent) {
    this.search();
  }

  search() {
    this.findItem(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  selectItem(item: ItemDTO) {
    this.selectedItem.emit(item);
  }
}
